import React from 'react';
import { message } from 'antd';
import { Prospect, TProspectInsurance } from 'types/common';
import useCurrentUser from 'hooks/useCurrentUser';
import {
    EEligibilityStatus,
    ELIGIBILITY_TIP_BY_BACKEND_STATUS,
    headlineToken,
    TClientEligibilityStateVo,
    TBackendEligibilityCheckVo,
    TEligibilityCheckHistoryRow,
    TEligibilityCheckVo,
    TPayerCandidate,
} from 'types/insuranceEligibility';
import {
    getEligibilityCheckHistory,
    getEligibilityPrecheck,
    requestUpdatedInsurance,
    runEligibilityCheck,
    runEligibilityDiscovery,
    runEligibilityRecheck,
} from 'api/eligibility';
import { adaptCheckVo, adaptDiscoveryCandidates, isUnmappedStatus } from 'api/eligibility/adapt';
import {
    deriveEligibilityView,
    getPrimaryInsurances,
    getSecondaryInsurances,
    indexCheckCards,
    latestCheckedInsuranceId,
} from 'utils/clientEligibility';
import { getUserId } from 'utils/localstore';
import { deriveTagFromCheck, isCarveOut, isPartial } from '../derive';
import { setDefaultInsurance } from 'api/prospect';
import { CARVE_TAG_TIP, OTHER_RESULT_TOAST, SET_DEFAULT_COPY, SYSTEM_ERROR_COPY } from '../copy';
import { ELIGIBILITY_STATUS_CONFIG } from 'components/EligibilityStatusTag';
import { TAaaEntry } from '../aaa';

// History 每页条数（后端 clamp 到 [1,50]）：一屏装得下、滚到底再追加
const HISTORY_PAGE_SIZE = 20;

// To-do "Insurance to re-check" 跳转带过来的 appointmentId 参数名（TodoListModal 写入）
const RECHECK_PARAM = 'recheckAppointmentId';
// recheck 只回行数，结果得等重拉的详情；详情请求失败时用这个兜底收掉 spinner，不让它一直转
const RECHECK_REFRESH_TIMEOUT = 20 * 1000;

// 用掉一次就把参数从 URL 上摘掉（保留 subTab 等其它参数）：刷新后后端名下已无待复查行，
// 再走 recheck 只会静默回 0，回落按保险行的手动 check 才是对的
const dropRecheckUrlParam = () => {
    try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has(RECHECK_PARAM)) return;
        url.searchParams.delete(RECHECK_PARAM);
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    } catch (e) {
        // URL 解析/替换失败不影响主流程
    }
};

// Assigned providers 卡片的一行（practice 视图）：identity 下的一位 provider 及其 sibling 记录
export type TAssignedProvider = {
    providerId: number;
    // 该 provider 名下的 sibling prospect（check / card request 的 clientId）
    prospectId?: number;
    name: string;
    credential?: string;
    avatar?: string;
    // 当前登录者本人（卡片印 You）
    self?: boolean;
    // 这位 provider 自己的最近一次 check 状态（与 Clients 列表同一套派生）
    status: EEligibilityStatus;
    // 同一标签按成因换 hover（如 NO_COVERAGE_DETAIL），与列表同源
    tipOverride?: string;
};

// 选定候选后发给患者的通知记录（unmapped 卡渲染 "已选 X · 已通知患者" 行；不触发 check）
export type TRoutedNotice = { payer: TPayerCandidate; sentAt: string };

export type TEligibilityApi = {
    enabled: boolean;
    hasInsurance: boolean;
    carrier: string;
    prospect?: Prospect;
    isGroupPractice?: boolean;
    // practice 视图的执行身份：归属 provider（GP detail 的 providerId = 最近一次 check 的那位）。
    // 动作接口/billing status 都以它为口径；my/solo 视图恒 undefined（请求层对 self 也不发头）
    overrideProviderId?: number;
    // header badge 用：本地没跑过 check 时沿用行数据状态（两个 surface 必须一致），跑过后按新结果派生
    tagStatus?: EEligibilityStatus;
    tagTipOverride?: string;
    stateVo?: TClientEligibilityStateVo;
    latestCheck?: TEligibilityCheckVo;
    checking: boolean;
    // 该患者今日（NY 时区）已跑过的 attempt 数，managed 每日 5 次上限用。值以后端
    // todayCheckCount 为准（每次 check 后重拉的详情会同步），前端不自增；recheck 不计入
    runsThisSession: number;
    // 下一次 Run check 走 To-do 带来的 per-appointment recheck：不占每日限额，上限闸要放行
    recheckPending: boolean;
    foldOpen: boolean;
    toggleFold: () => void;
    // targetInsuranceId：显式指定这次发往哪张（Re-check 指定结果卡那张；不传 = checkInsurance）。
    // announce：「查另一张」的那次，答案落地后 toast 点名那张保险与结果
    run: (targetInsuranceId?: number, opts?: { announce?: boolean }) => void;

    // ── Request insurance card 流（cardRequestedAt 仅会话内有效，刷新后可重发——已拍板不持久化）──
    cardRequestedAt?: string;
    cardRequestOpen: boolean;
    // 修复卡入口带 AAA 上下文（决定预填 note）；header 常驻按钮传 undefined
    cardRequestAaa?: TAaaEntry;
    openCardRequest: (aaa?: TAaaEntry) => void;
    closeCardRequest: () => void;
    sendCardRequest: (note: string, channels: Array<'chat' | 'text' | 'email'>) => Promise<void>;

    // ── unmapped payer / discovery 流 ──
    payerUnmatched?: { carrierOnFile?: string; insuranceId?: number };
    advancedLoading: boolean;
    advancedCandidates?: TPayerCandidate[];
    runAdvancedSearch: () => void;
    // 选定候选：不触发 check——只弹患者通知（RouteConfirmModal），发送后流程结束（已与产品拍板）
    routePick?: TPayerCandidate;
    pickPayer: (candidate: TPayerCandidate) => void;
    routedNotice?: TRoutedNotice;
    confirmRouteNotified: () => void;
    // 关掉患者通知弹窗但不记「已通知」：没发消息就等于没选，卡片退回候选列表让用户重选
    cancelRoutePick: () => void;

    // ── Assigned providers 切换（practice 视图；my/solo 恒空数组、selected 为 undefined）──
    // 切换换掉的是整个上下文：clientId / insuranceId / OVERRIDE-PROVIDER-ID 一起走
    providers: TAssignedProvider[];
    selectedProviderId?: number;
    selectProvider: (providerId: number) => void;
    // 选中 provider 名下的保险行（保险资料区按它渲染，与发出去的 insuranceId 是同一条）
    activeInsurances?: TProspectInsurance[];
    // 当前上下文的 clientId = 选中 provider 的 sibling prospect（my/solo 即患者本人）
    clientId?: number;

    // ── 多份 primary：check 发给哪张（2026-10-12 规则）──
    // 从没查过 → provider 选的那张（默认 default）；查过点 Re-check → 当前结果卡那张；
    // 失败后可以另选一张（仅 not enrolled，入口在结果卡）
    primaryInsurances: TProspectInsurance[];
    // 10/21：次保险也能手动查（选择卡 / 失败后「查另一张」都分组列出）
    secondaryInsurances: TProspectInsurance[];
    defaultInsurance?: TProspectInsurance;
    // 下一次 check 发往的那张保险（precheck / check / discovery 都以它为键）
    checkInsurance?: TProspectInsurance;
    // 每张卡最后一次 check（insuranceId → 摘要）；没查过的卡不在里面
    checkCards: Map<number, TBackendEligibilityCheckVo>;
    // 当前结果卡对应的那张（cards 里最新一条；没查过为 undefined）。Re-check 发往它
    cardInsuranceId?: number;
    selectInsurance: (insuranceId?: number) => void;
    // 把结果卡那张设为 default（set-default 接口），成功后重拉详情
    setDefault: () => Promise<void>;
    settingDefault: boolean;
    // 从没查过：idle 位置一律出选择卡（10/22：只有一张也出，分组标明是主是次）
    needsInsurancePick: boolean;

    // ── History（标题行按钮在 prosepectInfo，弹窗在模块内——沿用 card request 的分工）──
    historyOpen: boolean;
    // 首屏加载（换页追加时用 historyLoadingMore，表格不闪）
    historyLoading: boolean;
    historyLoadingMore: boolean;
    historyRows?: TEligibilityCheckHistoryRow[];
    historyHasMore: boolean;
    openHistory: () => void;
    closeHistory: () => void;
    loadMoreHistory: () => void;

    // 轻量重取 precheck（NPI/payer ID 设置保存后 setup 门槛变了），不重置本地流程态
    refreshState: () => Promise<void>;
};

const CHANNEL_MAP: Record<'chat' | 'text' | 'email', 'MESSAGE' | 'SMS' | 'EMAIL'> = {
    chat: 'MESSAGE',
    text: 'SMS',
    email: 'EMAIL',
};

// 行数据（latestEligibilityCheck，详情路径含 detail/failedCount/cashPay）→ 进页即渲染的 stateVo。
// practice 视图下这两个入参来自选中的那位 provider 的 sibling（见 TActiveContext），不是 identity 顶层。
// monthToDateCents 由 precheck 的 currentMonthFee 异步补；hasUpcomingBooking 待后端（二轮）
const buildStateVo = (
    backend: TBackendEligibilityCheckVo | undefined,
    // UNMAPPED 卡点名的档案保司：下一次 check 发往的那张（不是数组第一张）
    insuranceOnFile: TProspectInsurance | undefined,
): TClientEligibilityStateVo => {
    const unmapped = isUnmappedStatus(backend);
    // 二次失败（outreach）：连续失败 ≥2（拿到 PASSED-group status 后端置零）。cashPay 为 null
    // （无 upcoming PPA 预约）时 strip 照显、结尾走 no-cash 版
    const outreach =
        (backend?.failedCount ?? 0) >= 2 ? { stillFailing: true, cashPay: backend?.cashPay === true } : undefined;
    return {
        latestCheck: unmapped ? undefined : adaptCheckVo(backend),
        hasUpcomingBooking: false,
        recheckAllowed: true,
        monthToDateCents: 0,
        // 点名被挡下的那张：优先用 check 记录上的快照——「查另一张」选中别的卡后 insuranceOnFile 已不是它
        payerUnmatched: unmapped
            ? {
                  carrierOnFile: backend?.carrier || insuranceOnFile?.carrier,
                  insuranceId: backend?.insuranceId ?? insuranceOnFile?.id,
              }
            : undefined,
        outreach,
    };
};

// 数据 + check 状态机。prospect 传 undefined 时全 no-op（prosepectInfo 的 6 处复用点安全）。
// persona 不进这里——它只影响 CTA/计费展示，由容器层处理。
const useClientEligibilityCheck = (
    prospect: Prospect | undefined,
    isGroupPractice?: boolean,
    // check 跑完后重拉详情：新结果落到后端下发的 (provider, sibling) 上，切走再切回不会丢
    onRefetchDetail?: () => void,
    // To-do "Insurance to re-check" 带过来的 appointmentId（URL 参数）：本次 Run check 改走
    // per-appointment 的 recheck 接口。只用一次，用掉后回落按保险行的手动 check
    recheckAppointmentId?: number,
): TEligibilityApi => {
    const [currentUser] = useCurrentUser();
    const [stateVo, setStateVo] = React.useState<TClientEligibilityStateVo>();
    const [localCheck, setLocalCheck] = React.useState<TEligibilityCheckVo>();
    const [checking, setChecking] = React.useState(false);
    const [foldOpen, setFoldOpen] = React.useState(false);
    const [runsThisSession, setRunsThisSession] = React.useState(0);

    // 待用的 recheck appointmentId：拿到接口任一合法返回即清空（后端此时名下已无 NEED_PROVIDER_RECHECK
    // 行，再调只会一直回 0）。run() 读 ref（闭包不旧）；recheckPending 是给按钮上限闸用的渲染镜像，两者同步改
    const pendingRecheckRef = React.useRef<number | undefined>(recheckAppointmentId);
    const [recheckPending, setRecheckPending] = React.useState(!!recheckAppointmentId);
    const setPendingRecheck = (id: number | undefined) => {
        pendingRecheckRef.current = id;
        setRecheckPending(!!id);
    };
    // recheck 的结果不在响应里（只回行数），要等重拉的详情落地才能渲染：spinner 由下面那个
    // backendCheck effect 认领收尾，这个 ref 是认领凭证
    const awaitingRecheckRef = React.useRef(false);
    const recheckTimerRef = React.useRef<ReturnType<typeof setTimeout>>();

    const [cardRequestedAt, setCardRequestedAt] = React.useState<string>();
    const [cardRequestOpen, setCardRequestOpen] = React.useState(false);
    const [cardRequestAaa, setCardRequestAaa] = React.useState<TAaaEntry>();

    const [advancedLoading, setAdvancedLoading] = React.useState(false);
    const [advancedCandidates, setAdvancedCandidates] = React.useState<TPayerCandidate[]>();
    const [routePick, setRoutePick] = React.useState<TPayerCandidate>();
    const [routedNotice, setRoutedNotice] = React.useState<TRoutedNotice>();

    const [historyOpen, setHistoryOpen] = React.useState(false);
    const [historyLoading, setHistoryLoading] = React.useState(false);
    const [historyLoadingMore, setHistoryLoadingMore] = React.useState(false);
    const [historyRows, setHistoryRows] = React.useState<TEligibilityCheckHistoryRow[]>();
    const [historyTotal, setHistoryTotal] = React.useState(0);
    // 已取到第几页（滚动到底追加下一页）
    const historyPageRef = React.useRef(0);

    // ── 当前上下文：practice 视图是选中的那位 provider 的 (sibling prospect, 保险, 身份) ──
    // identity 底下每位 provider 一条 sibling prospect，各自有保险行和 check 记录，所以切换 provider
    // 换掉的不只是 OVERRIDE-PROVIDER-ID，还有 clientId 与 insuranceId——三者必须成对，
    // 拿 A 的 insuranceId 配 B 的身份会被后端归属校验挡掉
    const boundProviders = React.useMemo(
        () => (isGroupPractice ? prospect?.boundProviders || [] : []),
        [isGroupPractice, prospect],
    );
    const providers: TAssignedProvider[] = React.useMemo(
        () =>
            boundProviders
                .filter((bp) => !!bp.providerId)
                .map((bp) => {
                    const view = deriveEligibilityView(bp.latestEligibilityCheck, (bp.insurances?.length || 0) > 0);
                    return {
                        providerId: bp.providerId!,
                        prospectId: bp.prospectId,
                        name: `${bp.firstName || ''} ${bp.lastName || ''}`.trim(),
                        credential: bp.credential,
                        avatar: bp.avatar,
                        self: bp.providerId === getUserId(),
                        // 每张卡自带这位 provider 自己的状态：owner 一眼看出谁还没查
                        status: view.status,
                        tipOverride: view.tipOverride,
                    };
                }),
        [boundProviders],
    );

    // 一位都没查过时不预选（原型 09/15）：check 要以某位 provider 的 payer ID 发出，
    // 没有上一次 check 就没有可复用的身份，必须由人来挑
    const [selectedProviderId, setSelectedProviderId] = React.useState<number>();
    const active = React.useMemo(() => {
        // boundProviders 缺席（solo/my 视图，或 GP 详情尚未下发该字段）→ 退回 identity 顶层，
        // 即「永远是 primary sibling」的老行为，不至于因为少一个字段就整块失能
        if (!isGroupPractice || boundProviders.length === 0) {
            return {
                clientId: prospect?.id,
                insurances: prospect?.insurances,
                backendCheck: prospect?.latestEligibilityCheck,
                checkCards: prospect?.eligibilityCheckCards,
                overrideProviderId: (isGroupPractice ? prospect?.providerId : undefined) as number | undefined,
            };
        }
        const bp = boundProviders.find((p) => p.providerId === selectedProviderId);
        return {
            clientId: bp?.prospectId,
            insurances: bp?.insurances,
            backendCheck: bp?.latestEligibilityCheck,
            checkCards: bp?.eligibilityCheckCards,
            overrideProviderId: bp?.providerId,
        };
    }, [isGroupPractice, prospect, boundProviders, selectedProviderId]);

    const prospectId = active.clientId;
    const { overrideProviderId } = active;

    // ── check 发给哪张保险 ──
    // 后端所有 check/precheck/discovery 接口以 prospect_insurance.id 为键。依次取：
    // ① provider 刚选的那张（从没查过的选择卡 / 失败后的「查另一张」）；
    // ② 当前结果卡那张——latestEligibilityCheck 是最近一次 check，直接读它的 insuranceId（缺省时按时间从 cards 反推），
    //    Re-check 必须查它，否则结果卡会被另一张保险的答案顶掉；
    // ③ default primary；④ 老数据兜底。
    // 以前一律取 insurances[0]（后端数组首位，可能是非 default 甚至 secondary）
    const primaryInsurances = React.useMemo(() => getPrimaryInsurances(active.insurances), [active.insurances]);
    const secondaryInsurances = React.useMemo(() => getSecondaryInsurances(active.insurances), [active.insurances]);
    const checkCards = React.useMemo(() => indexCheckCards(active.checkCards), [active.checkCards]);
    const lastCheckedId = React.useMemo(
        () => active.backendCheck?.insuranceId ?? latestCheckedInsuranceId(active.checkCards),
        [active.backendCheck, active.checkCards],
    );
    const [selectedInsuranceId, setSelectedInsuranceId] = React.useState<number>();
    const checkInsurance = React.useMemo(() => {
        const all = active.insurances || [];
        const byId = (id?: number) => (id == null ? undefined : all.find((ins) => ins.id === id));
        // 结果卡那张只要还在档案上就是发往对象（secondary 也能查）；被删了才回到 default primary
        // 档案上只有次保险时落到 default 次保险（没有 primary 可回）
        return (
            byId(selectedInsuranceId) || byId(lastCheckedId) || primaryInsurances[0] || secondaryInsurances[0] || all[0]
        );
    }, [active.insurances, selectedInsuranceId, lastCheckedId, primaryInsurances, secondaryInsurances]);
    const insuranceId = checkInsurance?.id;
    // precheck 的输入：发往哪张 + 它的 carrier / plan + 患者所在州（template gap 按 state × carrier 判）。
    // 编辑保险 / Personal info 后详情重拉，id 不变但这几项变了也要重取，否则横幅停在旧数据上
    const precheckKey = insuranceId
        ? [insuranceId, checkInsurance?.carrier, checkInsurance?.plan, prospect?.state].join('|')
        : undefined;
    // 最近一次 precheck 是按哪组输入取的（重取的去重凭证；也挡慢回包覆盖新结果）
    const precheckKeyRef = React.useRef<string>();

    // 默认选中：有人查过就落在 boundProviders[0]（= primary sibling，与 GP 列表同源）；
    // 一位都没查过则保持未选，模块体出「先选一位 provider」提示条。
    // 已选中的人还在名单里就不动——重拉详情不该把用户的选择顶掉
    React.useEffect(() => {
        if (!isGroupPractice) return;
        if (selectedProviderId && boundProviders.some((bp) => bp.providerId === selectedProviderId)) return;
        const anyChecked = boundProviders.some((bp) => !!bp.latestEligibilityCheck?.latestCheckedAt);
        setSelectedProviderId(anyChecked ? boundProviders[0]?.providerId : undefined);
    }, [isGroupPractice, boundProviders, selectedProviderId]);

    // precheck 的 setup flags 合并进 stateVo（两种 persona 都消费——enrolled 也要 setup，
    // enroll 只豁免 cost modal，2026-09-15 拍板）。
    // eligibilityCheckEnabled=false（org 开关未开）按「NPI 未设」处理：/setup 保存 NPI 时顺带打开开关
    const loadPrecheck = React.useCallback(async () => {
        if (!insuranceId) return;
        const pre = await getEligibilityPrecheck(insuranceId, overrideProviderId);
        // 选择卡上来回点：慢的那次回包不能盖掉当前选中那张的 gap
        if (!pre || precheckKeyRef.current !== precheckKey) return;
        setStateVo((prev) => {
            if (!prev) return prev;
            // 两个独立的 gap，展示上互斥、template 优先：
            // ① provider 没声明接这家保险 → 格子不存在，出 template gap 横幅（后端不 gate /check，
            //    拦是前端策略；payerIdMapped 是真是假都轮不到它说话——payer ID 填了也没用）；
            // ② 行在、payer ID 空 → 原 payer ID 横幅 / 设置弹窗。
            // providerAcceptsInsurance 未下发（undefined）时按 ② 处理，与今天行为一致。
            // 两者都不适用于 UNMAPPED 的那张：请求压根没发出去，屏幕归 advanced search 流。
            // 但 precheck 若是按「查另一张」选中的别的卡取的，照常判——否则点 Check 时 template / payer ID 都不拦
            const unmatchedId = prev.payerUnmatched?.insuranceId;
            const precheckIsUnmatched = !!prev.payerUnmatched && (unmatchedId == null || unmatchedId === insuranceId);
            const canAct = !precheckIsUnmatched && !!prospect;
            const payerGap = pre.payerIdMapped === false && canAct;
            const gapCarrier = checkInsurance?.carrier || '';
            const gapState = prospect?.state || '';
            const templateMissing =
                canAct && pre.providerAcceptsInsurance === false ? { carrier: gapCarrier, state: gapState } : undefined;
            const payerIdMissing =
                payerGap && !templateMissing
                    ? {
                          carrier: gapCarrier,
                          state: gapState,
                          // 本次 payer 走的通道由后端判（Medicare → GOVERNMENT）：决定 blocking
                          // 格子的 "· this check" 落在哪个输入框
                          kind: pre.payerIdKind === 'GOVERNMENT' ? ('government' as const) : ('commercial' as const),
                      }
                    : undefined;
            return {
                ...prev,
                // 本 NY 日历月已产生的 billable 费用（美元 → 美分）：cost modal 的累计行
                monthToDateCents: Math.round((pre.currentMonthFee || 0) * 100),
                setup: {
                    billingNpiSet: !!pre.eligibilityCheckEnabled && !!pre.billingNpiConfigured,
                    profileNpi: pre.profileNpi ? { npi: pre.profileNpi, name: '' } : undefined,
                    payerIdMissing,
                    templateMissing,
                },
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [precheckKey, prospectId, overrideProviderId]);

    React.useEffect(() => {
        // 换病人、或 practice 视图切了 provider（clientId 跟着换）：清空全部本地状态，
        // 折叠态/流程态绝不继承——上一位 provider 的结果不能挂在这一位名下
        setStateVo(undefined);
        setLocalCheck(undefined);
        setFoldOpen(false);
        setChecking(false);
        setCardRequestedAt(undefined);
        setCardRequestOpen(false);
        setCardRequestAaa(undefined);
        setAdvancedLoading(false);
        setAdvancedCandidates(undefined);
        setRoutePick(undefined);
        setRoutedNotice(undefined);
        setHistoryOpen(false);
        setHistoryRows(undefined);
        setHistoryLoading(false);
        setHistoryLoadingMore(false);
        setHistoryTotal(0);
        historyPageRef.current = 0;
        // 保险选择是这位患者（这位 provider 名下）的，换人不继承
        setSelectedInsuranceId(undefined);
        // 日限计数以后端 todayCheckCount（NY 时区今日 attempt 数，详情路径下发）为初始值，跨会话准确
        setRunsThisSession(active.backendCheck?.todayCheckCount || 0);
        // practice 视图未选 provider 时没有上下文可渲染（模块体出「先选一位」提示条）
        if (!prospect || !prospectId) return;
        // 行数据同步可得，先渲染；precheck 异步补 setup 门槛与本月费用
        setStateVo(buildStateVo(active.backendCheck, checkInsurance));
        // UNMAPPED 且历史上跑过 discovery：候选随详情下发，预填避免重复付 $1.50
        const prefill = active.backendCheck?.discovery;
        if (isUnmappedStatus(active.backendCheck) && prefill?.length) {
            setAdvancedCandidates(adaptDiscoveryCandidates(prefill));
        }
        precheckKeyRef.current = precheckKey;
        loadPrecheck();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prospectId, isGroupPractice]);

    // 重拉详情后（check 跑完、或页面其它保存触发）用后端最新数据重建 stateVo：结果从此归后端所有，
    // 切走再切回还在。localCheck 只是网络往返期间的即时渲染层，服务端数据一到就让位。
    // 展开态不动——刚查完的卡不该因为一次静默刷新自己折起来
    const backendCheckRef = React.useRef<TBackendEligibilityCheckVo>();
    React.useEffect(() => {
        if (!prospectId) return;
        if (backendCheckRef.current === active.backendCheck) return;
        backendCheckRef.current = active.backendCheck;
        setStateVo((prev) => {
            const next = buildStateVo(active.backendCheck, checkInsurance);
            // precheck 派生的那几项（setup 门槛、本月累计）不在 check 数据里，保留
            return prev
                ? {
                      ...next,
                      setup: prev.setup,
                      monthToDateCents: prev.monthToDateCents,
                  }
                : next;
        });
        setLocalCheck(undefined);
        // 新结果落地：选择用完了，之后的 Re-check 回到「当前结果卡那张」（cards 里最新那条）
        setSelectedInsuranceId(undefined);
        // 日限次数以后端为准（详情路径下发的 todayCheckCount 是唯一真相，前端不自增）；
        // 字段缺省时保留原值——归零会让限额闸无声地重新打开
        const todayCount = active.backendCheck?.todayCheckCount;
        if (typeof todayCount === 'number') setRunsThisSession(todayCount);
        // recheck 等的就是这次落地：结果已经进 stateVo，收掉 spinner 并展开结果卡
        if (awaitingRecheckRef.current) {
            awaitingRecheckRef.current = false;
            if (recheckTimerRef.current) clearTimeout(recheckTimerRef.current);
            setChecking(false);
            setFoldOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active.backendCheck, prospectId]);

    // 换了要发往的保险（选择卡 / 「查另一张」/ 结果落到另一张），或编辑保险 / 州之后详情重拉：
    // template gap 与 payer ID gap 都按这组输入算，precheck 重取。换人时重置 effect 已经取过，这里按 ref 去重
    React.useEffect(() => {
        if (!prospectId || !precheckKey || precheckKeyRef.current === precheckKey) return;
        precheckKeyRef.current = precheckKey;
        loadPrecheck();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [precheckKey]);

    // 入参换了（从另一条 to-do 行进来，组件没重挂）：重新武装。用掉后置 undefined 不会被这里顶回来
    React.useEffect(() => {
        pendingRecheckRef.current = recheckAppointmentId;
        setRecheckPending(!!recheckAppointmentId);
    }, [recheckAppointmentId]);

    React.useEffect(
        () => () => {
            if (recheckTimerRef.current) clearTimeout(recheckTimerRef.current);
        },
        [],
    );

    const listView = prospect
        ? deriveEligibilityView(active.backendCheck, (active.insurances?.length || 0) > 0, checkInsurance?.carrier)
        : undefined;
    // 直接看保险行：状态词汇表现在优先报「最后一次检查的结果」，保险被删的患者也会显示
    // 那次结果，不能再拿 tag 反推有没有保险（模块能不能动、insuranceId 有没有都靠它）
    const hasInsurance = !!prospect && (active.insurances?.length || 0) > 0;
    const latestCheck = localCheck || stateVo?.latestCheck;

    // run() 回调里判「是否已有完成的结果」用 ref 取当前值（回调 deps 刻意不含 check 态，闭包会旧）
    const latestCheckRef = React.useRef<TEligibilityCheckVo>();
    latestCheckRef.current = latestCheck;

    const tagStatus = !prospect
        ? undefined
        : (localCheck && deriveTagFromCheck(localCheck, hasInsurance)) || listView?.status;
    // 同一个 tag 两种成因，hover 要点名实际那个：carve-out 才换（「payer 没回 copay」对它是假话）；
    // partial 同时成立时不换——那时缺数字的原因确实是 payer 没回（原型 tagTip 的 !partial 条件）
    const carveTip =
        tagStatus === EEligibilityStatus.NO_COST_ESTIMATE &&
        latestCheck?.result &&
        isCarveOut(latestCheck.result) &&
        !isPartial(latestCheck.result)
            ? CARVE_TAG_TIP
            : undefined;
    // 按 token 换 tip（NO_COVERAGE_DETAIL 等）：与列表同一张表
    const tagTipOverride =
        carveTip || ELIGIBILITY_TIP_BY_BACKEND_STATUS[headlineToken(latestCheck?.statusTokens || []) || ''];

    const run = React.useCallback(
        (targetInsuranceId?: number, opts?: { announce?: boolean }) => {
            if (checking) return;
            // To-do "Insurance to re-check" 进来的那一次：键是 appointment 而不是保险行——后端按
            // appointmentId 定位患者，把名下所有 NEED_PROVIDER_RECHECK 行各跑一次同样的 270。
            // 响应只回处理行数，结果得从重拉的详情里看（没有 group 场景，不带 override 头）
            const recheckId = pendingRecheckRef.current;
            if (recheckId) {
                setChecking(true);
                (async () => {
                    const count = await runEligibilityRecheck(recheckId);
                    // 请求失败：Request 层已报错，卡面不动；appointmentId 留着，可以再点一次重试
                    if (typeof count !== 'number') {
                        setChecking(false);
                        return;
                    }
                    setPendingRecheck(undefined);
                    dropRecheckUrlParam();
                    // 0 = 名下已无待复查行（别处已经查过）：没有新结果可看，到此为止
                    if (count === 0) {
                        setChecking(false);
                        return;
                    }
                    // 结果一律按后端最新记录渲染：spinner 交给 backendCheck effect 收尾
                    awaitingRecheckRef.current = true;
                    recheckTimerRef.current = setTimeout(() => {
                        awaitingRecheckRef.current = false;
                        setChecking(false);
                    }, RECHECK_REFRESH_TIMEOUT);
                    onRefetchDetail?.();
                })();
                return;
            }
            const sendId = targetInsuranceId ?? insuranceId;
            if (!sendId) return;
            const sentInsurance = (active.insurances || []).find((ins) => ins.id === sendId);
            setChecking(true);
            (async () => {
                const backend = await runEligibilityCheck(sendId, overrideProviderId);
                setChecking(false);
                if (!backend) return;
                if (isUnmappedStatus(backend)) {
                    // check 返回 UNMAPPED：切到 unmapped 卡（请求没发出去，无结果可渲染）
                    setLocalCheck(undefined);
                    setStateVo((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  latestCheck: undefined,
                                  payerUnmatched: { carrierOnFile: sentInsurance?.carrier, insuranceId: sendId },
                              }
                            : prev,
                    );
                    return;
                }
                const adapted = adaptCheckVo(backend);
                // SYSTEM_ERROR 是事件不是状态（原型 §6）：请求根本没到达 payer，患者的 coverage
                // 没有任何变化。已有完成的结果（含 AAA 拒识；上一张 system error 卡不算）时卡面
                // 一动不动，点击时刻的 toast 是全部反馈；从未有过结果才让失败独占卡面
                if (adapted?.statusTokens[0] === 'SYSTEM_ERROR') {
                    message.info(SYSTEM_ERROR_COPY.toast);
                    const prev = latestCheckRef.current;
                    if (prev && prev.statusTokens?.[0] !== 'SYSTEM_ERROR') {
                        setFoldOpen(true);
                        return;
                    }
                }
                // /check 路径不回 operator 字段：practice 视图刚跑完的标注用登录人名字补
                // （刷新后由 GP detail 的 operator 字段接管）；my/solo 视图不显示 by，不补
                if (adapted && isGroupPractice) {
                    adapted.checkedBy =
                        adapted.checkedBy ||
                        `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() ||
                        undefined;
                }
                // /check 回包可能不带 insuranceId：答的就是这次发往的那张
                if (adapted && adapted.insuranceId == null) adapted.insuranceId = sendId;
                setLocalCheck(adapted);
                // 仅 provider 主动触发的 check 完成后自动展开一次
                setFoldOpen(true);
                // 「查另一张」：点名那张保险和它的答案（原型 runOtherCheck 的 toast）
                if (opts?.announce && adapted) {
                    const name = [sentInsurance?.carrier, sentInsurance?.plan].filter(Boolean).join(' ') || 'This plan';
                    const tag = deriveTagFromCheck(adapted, true);
                    message.info(
                        tag === EEligibilityStatus.ELIGIBLE
                            ? OTHER_RESULT_TOAST.active(name)
                            : OTHER_RESULT_TOAST.other(name, ELIGIBILITY_STATUS_CONFIG[tag]?.label || 'checked'),
                    );
                }
                // 重拉详情：新结果写回后端下发的 (provider, sibling)，practice 视图切走再切回不会丢
                onRefetchDetail?.();
            })();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [insuranceId, checking, prospectId, overrideProviderId, isGroupPractice, currentUser, onRefetchDetail],
    );

    const openCardRequest = React.useCallback((aaa?: TAaaEntry) => {
        setCardRequestAaa(aaa);
        setCardRequestOpen(true);
    }, []);

    const sendCardRequest = React.useCallback(
        async (note: string, channels: Array<'chat' | 'text' | 'email'>) => {
            if (!prospectId) return;
            // 后端要求至少一个渠道；全不勾 = 只做本地记录（"Request saved. No message was sent."）
            if (channels.length) {
                await requestUpdatedInsurance(
                    prospectId,
                    {
                        messageToPatient: note,
                        channels: channels.map((c) => CHANNEL_MAP[c]),
                    },
                    overrideProviderId,
                );
            }
            setCardRequestedAt(new Date().toISOString());
            setCardRequestOpen(false);
        },
        [prospectId, overrideProviderId],
    );

    const runAdvancedSearch = React.useCallback(() => {
        // 搜的是匹配不上的那张，不是「查另一张」里选中的
        const searchId = stateVo?.payerUnmatched?.insuranceId ?? insuranceId;
        if (!searchId || advancedLoading) return;
        setAdvancedLoading(true);
        (async () => {
            const backend = await runEligibilityDiscovery(searchId, overrideProviderId);
            setAdvancedLoading(false);
            if (!backend) return;
            setAdvancedCandidates(adaptDiscoveryCandidates(backend.discovery));
            // 付费搜索的结果自动揭示（provider 正盯着它）
            setFoldOpen(true);
        })();
    }, [insuranceId, advancedLoading, overrideProviderId, stateVo?.payerUnmatched?.insuranceId]);

    // 选定候选：只开患者通知弹窗；发送（confirmRouteNotified）后流程结束，UNMAPPED 状态保持
    const pickPayer = React.useCallback((candidate: TPayerCandidate) => {
        setRoutePick(candidate);
    }, []);

    const confirmRouteNotified = React.useCallback(() => {
        setRoutePick((picked) => {
            if (picked) setRoutedNotice({ payer: picked, sentAt: new Date().toISOString() });
            return undefined;
        });
    }, []);

    // 关闭而非发送：不写 routedNotice，选中状态整个丢掉，回到「还没选 payer」
    const cancelRoutePick = React.useCallback(() => {
        setRoutePick(undefined);
    }, []);

    // 每次打开都从第一页重取：期间可能刚跑过一次 check，缓存一份旧流水没有意义
    const fetchHistoryPage = React.useCallback(
        async (next: number) => {
            if (!prospectId) return;
            const { rows, total } = await getEligibilityCheckHistory(
                prospectId,
                { current: next, size: HISTORY_PAGE_SIZE },
                overrideProviderId,
            );
            historyPageRef.current = next;
            setHistoryTotal(total);
            setHistoryRows((prev) => (next === 1 ? rows : [...(prev || []), ...rows]));
        },
        [prospectId, overrideProviderId],
    );

    const openHistory = React.useCallback(() => {
        setHistoryOpen(true);
        setHistoryRows(undefined);
        setHistoryTotal(0);
        historyPageRef.current = 0;
        setHistoryLoading(true);
        (async () => {
            try {
                await fetchHistoryPage(1);
            } finally {
                setHistoryLoading(false);
            }
        })();
    }, [fetchHistoryPage]);

    // 滚到底触发：并发与越界都在这里挡住，弹窗只管喊
    const loadMoreHistory = React.useCallback(() => {
        if (historyLoading || historyLoadingMore) return;
        const loaded = historyRows?.length || 0;
        if (!loaded || loaded >= historyTotal) return;
        setHistoryLoadingMore(true);
        (async () => {
            try {
                await fetchHistoryPage(historyPageRef.current + 1);
            } finally {
                setHistoryLoadingMore(false);
            }
        })();
    }, [fetchHistoryPage, historyLoading, historyLoadingMore, historyRows, historyTotal]);

    // 切换 provider：只改选择，其余（本地流程态清空、stateVo 重建、precheck 重取）由
    // clientId 变化驱动的重置 effect 统一处理，避免两处各清一半
    const selectProvider = React.useCallback((providerId: number) => {
        setSelectedProviderId(providerId);
    }, []);

    const [settingDefault, setSettingDefault] = React.useState(false);
    const setDefault = React.useCallback(async () => {
        const target = (active.insurances || []).find((ins) => ins.id === lastCheckedId);
        if (target?.id == null || settingDefault) return;
        setSettingDefault(true);
        try {
            const res: any = await setDefaultInsurance(target.id, overrideProviderId);
            if (res?.error) return;
            message.success(SET_DEFAULT_COPY.done([target.carrier, target.plan].filter(Boolean).join(' ')));
            onRefetchDetail?.();
        } finally {
            setSettingDefault(false);
        }
    }, [active.insurances, lastCheckedId, settingDefault, overrideProviderId, onRefetchDetail]);

    const refreshState = React.useCallback(async () => {
        await loadPrecheck();
    }, [loadPrecheck]);

    return {
        enabled: !!prospect,
        hasInsurance,
        carrier: listView?.carrier || 'the payer',
        prospect,
        isGroupPractice,
        overrideProviderId,
        tagStatus,
        tagTipOverride,
        stateVo,
        latestCheck,
        checking,
        runsThisSession,
        recheckPending,
        foldOpen,
        toggleFold: () => setFoldOpen((v) => !v),
        run,
        cardRequestedAt,
        cardRequestOpen,
        cardRequestAaa,
        openCardRequest,
        closeCardRequest: () => setCardRequestOpen(false),
        sendCardRequest,
        payerUnmatched: stateVo?.payerUnmatched,
        advancedLoading,
        advancedCandidates,
        runAdvancedSearch,
        routePick,
        pickPayer,
        routedNotice,
        confirmRouteNotified,
        cancelRoutePick,
        providers,
        selectedProviderId,
        selectProvider,
        activeInsurances: active.insurances,
        clientId: prospectId,
        primaryInsurances,
        secondaryInsurances,
        defaultInsurance: primaryInsurances[0],
        checkInsurance,
        checkCards,
        cardInsuranceId: lastCheckedId,
        selectInsurance: setSelectedInsuranceId,
        setDefault,
        settingDefault,
        needsInsurancePick: hasInsurance && !latestCheck && !stateVo?.payerUnmatched,
        historyOpen,
        historyLoading,
        historyLoadingMore,
        historyRows,
        historyHasMore: (historyRows?.length || 0) < historyTotal,
        openHistory,
        closeHistory: () => setHistoryOpen(false),
        loadMoreHistory,
        refreshState,
    };
};

export default useClientEligibilityCheck;
